/*
"Mes cartes" — table of the authenticated user's uploaded card reads,
most recent first. Each row links to the detail page.

Paginated: ?page=&pageSize= are sent to the server, prev/next buttons
+ "Page N / M" indicator are rendered below the table.

Filterable: the bar above the table maps 1:1 onto the server-side
filter params on GET /api/contents (serialNumber substring, cardType
exact, dataModel exact, folderPath prefix, readDate range). Typing
in a text field is debounced 250ms so each keystroke doesn't issue a
request; date changes apply immediately. Any filter change resets
the view to page 1.

URL-persistent: filter state is mirrored to the URL search string
(e.g. /cards?serialNumber=3B8F&cardType=CALYPSO_PRIME) so a filtered
view is bookmarkable and shareable. URL writes use replace=true so
each keystroke doesn't push a new history entry.
*/

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api, apiBlob, triggerDownload } from "../lib/api";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { ErrorState } from "../components/ErrorState";
import { Skeleton, SkeletonRows } from "../components/Skeleton";

const kPageSizeDefault	= 50;					// Initial rows per page
const kPageSizeOptions	= [25, 50, 100, 200];	// Selector choices
const kFilterDebounceMs	= 250;					// Settle delay before re-issuing a filtered request

// Enum values mirrored from the server-side Zod schemas in
// contentRoutes.ts. Kept in this order for the dropdown so the
// most common types / models float to the top. Anytime the server
// adds a value here, update both lists.
const kCardTypes	= ["CALYPSO_PRIME", "CALYPSO_LIGHT", "CALYPSO_BASIC", "ST25"] as const;
const kDataModels	= ["INTERCODE_2_2", "MOBIB_4_2_2", "MOBIB_5_4_1", "INTERTIC", "MOBIB_3_0", "MEXICO", "ILE_DE_FRANCE", "UNDEFINED"] as const;

interface CardSummary
{
	id:				string;	// Server-assigned identifier
	serialNumber:	string;	// Hex serial as captured from SELECT
	cardType:		string;	// CALYPSO_PRIME, ST25, …
	dataModel:		string;	// INTERCODE_2_2, …
	readDate:		number;	// ms since epoch
	folderPath:		string;	// Logical folder
	createdAt:		number;	// Persisted at (ms since epoch)
	updatedAt:		number;	// Last refresh (ms since epoch)
}

interface CardsPageResponse
{
	items:		CardSummary[];	// Paginated rows
	total:		number;			// Unpaginated total
	page:		number;			// Echoed page (after default + clamp)
	pageSize:	number;			// Echoed pageSize
}

interface Filters
{
	serialNumber:	string;	// Substring (sent only when non-blank)
	cardType:		string;	// Exact match
	dataModel:		string;	// Exact match
	folderPath:		string;	// Prefix match
	readDateFrom:	string;	// "YYYY-MM-DD" or "" for unset
	readDateTo:		string;	// "YYYY-MM-DD" or "" for unset
}

const kEmptyFilters: Filters =	// Reset target for "Effacer les filtres"
{
	serialNumber:	"",
	cardType:		"",
	dataModel:		"",
	folderPath:		"",
	readDateFrom:	"",
	readDateTo:		"",
};

export function CardsPage(): JSX.Element
{
	const [vSearchParams, fSetSearchParams] = useSearchParams();
	const fConfirm = useConfirmDialog();
	const { t }		= useTranslation("cards");
	const { t: tc }	= useTranslation("common");

	const [fCards, fSetCards]		= useState<CardSummary[] | null>(null);
	const [fTotal, fSetTotal]		= useState<number>(0);
	const [fPage,  fSetPage]		= useState<number>(1);
	const [fError, fSetError]		= useState<string | null>(null);
	const [fDeletingId, fSetDeletingId]	= useState<string | null>(null);
	// Hydrate filter state from the URL once on mount so a bookmarked
	// /cards?serialNumber=… link lands on the corresponding view. After
	// that we keep the URL in sync via fSetSearchParams below.
	const [fFilters, fSetFilters]	= useState<Filters>(() => filtersFromSearchParams(vSearchParams));
	const [fPageSize, fSetPageSize]	= useState<number>(() => readPageSizeFromParams(vSearchParams));

	// Latest filter snapshot kept in a ref so deleteCard / pager handlers
	// can issue a reload using the active filter set without joining the
	// debounce dependency chain.
	const fFiltersRef	= useRef<Filters>(fFilters);
	const fPageSizeRef	= useRef<number>(fPageSize);

	useEffect(() =>
	{
		fFiltersRef.current = fFilters;
	}, [fFilters]);

	useEffect(() =>
	{
		fPageSizeRef.current = fPageSize;
	}, [fPageSize]);

	// Mirror filter + pageSize state to the URL on every edit.
	// replace:true keeps the back button useful (one click leaves the
	// cards page entirely rather than scrubbing through every keystroke).
	useEffect(() =>
	{
		fSetSearchParams(filtersToSearchParams(fFilters, fPageSize), { replace: true });
		// fSetSearchParams identity changes per render; only react to
		// filter / pageSize edits, not router internals.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fFilters, fPageSize]);

	const fLoad = useCallback((inPage: number, inFilters: Filters, inPageSize: number) =>
	{
		const vQuery = buildQuery(inPage, inFilters, inPageSize);	// "?page=…&pageSize=…[&filters…]"

		api<CardsPageResponse>("GET", `/contents${vQuery}`)
			.then((vResp) =>
			{
				fSetCards(vResp.items);
				fSetTotal(vResp.total);
				fSetPage(vResp.page);
			})
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
	}, []);

	// Filter / pageSize changes always reset to page 1. Debounced so a
	// user typing in the serial-number field doesn't trigger a request
	// per keystroke; pageSize changes go through the same debounce, but
	// since the selector commits in one click the delay is invisible.
	useEffect(() =>
	{
		const vTimer = window.setTimeout(() => fLoad(1, fFilters, fPageSize), kFilterDebounceMs);

		return () => window.clearTimeout(vTimer);
	}, [fFilters, fPageSize, fLoad]);

	/*
	Asks for confirmation, then DELETE /contents/:id and removes the card
	from the local list on success. Reports any failure inline.
	*/
	async function deleteCard(
		inCard:	CardSummary	// Card to delete
	): Promise<void>
	{
		const vOk = await fConfirm.confirm(
			{
				title:			t("delete_dialog_title"),
				entity:			inCard.serialNumber,
				message:		t("delete_dialog_body"),
				confirmLabel:	t("delete_dialog_button"),
			}
		);

		if (!vOk)
		{
			return;
		}

		fSetDeletingId(inCard.id);

		api<void>("DELETE", `/contents/${inCard.id}`)
			.then(() =>
			{
				// Reload the current page (with current filters / pageSize)
				// rather than splicing locally — the row removal may shift
				// later pages forward and the total count needs refreshing.
				fLoad(fPage, fFiltersRef.current, fPageSizeRef.current);
			})
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			})
			.finally(() => fSetDeletingId(null));
	}

	if (fError !== null)
	{
		return (
			<ErrorState
				message={fError}
				onRetry={() => { fSetError(null); fLoad(fPage, fFiltersRef.current, fPageSizeRef.current); }}
			/>
		);
	}

	const vAnyFilterActive =	// True when at least one filter has a value
		fFilters.serialNumber !== "" ||
		fFilters.cardType !== "" ||
		fFilters.dataModel !== "" ||
		fFilters.folderPath !== "" ||
		fFilters.readDateFrom !== "" ||
		fFilters.readDateTo !== "";

	const vTotalPages = Math.max(1, Math.ceil(fTotal / fPageSize));	// Total pages for the footer

	const fHandleExport = async (inFormat: "csv" | "xlsx") =>
	{
		// Build the same filter querystring as fLoad, minus pagination
		// (the server returns the full filtered set up to its 10 k cap).
		const vParams = new URLSearchParams();

		vParams.set("format", inFormat);

		if (fFilters.serialNumber.trim() !== "") { vParams.set("serialNumber", fFilters.serialNumber.trim()); }
		if (fFilters.cardType.trim()     !== "") { vParams.set("cardType",     fFilters.cardType.trim()); }
		if (fFilters.dataModel.trim()    !== "") { vParams.set("dataModel",    fFilters.dataModel.trim()); }
		if (fFilters.folderPath.trim()   !== "") { vParams.set("folderPath",   fFilters.folderPath.trim()); }

		const vFromMs = dateInputToStartMs(fFilters.readDateFrom);
		const vToMs   = dateInputToEndMs  (fFilters.readDateTo);

		if (vFromMs !== null) { vParams.set("readDateFrom", String(vFromMs)); }
		if (vToMs   !== null) { vParams.set("readDateTo",   String(vToMs)); }

		try
		{
			const { blob: vBlob, filename: vFilename } = await apiBlob(`/contents/export?${vParams.toString()}`);
			const vStamp	= new Date().toISOString().slice(0, 10);
			const vFallback	= `nfcreader-cards-${vStamp}.${inFormat}`;

			triggerDownload(vBlob, vFilename ?? vFallback);
		}
		catch (vErr)
		{
			alert(vErr instanceof ApiError ? vErr.message : tc("error_network"));
		}
	};

	return (
		<>
		<div className="card">
			<div className="cards-header">
				<h2>{t("title_count", { total: fTotal })}</h2>
				<div className="cards-header__actions">
					<button
						type="button"
						className="button button--ghost"
						onClick={() => fHandleExport("csv")}
						disabled={fCards === null || fCards.length === 0}
					>
						{t("export_csv")}
					</button>
				</div>
			</div>

			<FilterBar
				filters={fFilters}
				onChange={fSetFilters}
				anyActive={vAnyFilterActive}
			/>

			{fCards === null
				? <div><Skeleton variant="title" widthPercent={30} /><SkeletonRows count={6} /></div>
				: fCards.length === 0
					? <p className="muted">
						{vAnyFilterActive
							? t("no_cards_with_filters")
							: t("no_cards")}
					</p>
					: (
						<>
							<table>
								<thead>
									<tr>
										<th>{t("col_serial")}</th>
										<th>{t("col_type")}</th>
										<th>{t("col_model")}</th>
										<th>{t("col_read_on")}</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{fCards.map((c) => (
										<tr key={c.id}>
											<td>
												<Link to={`/cards/${c.id}`} className="cards-row__serial">
													<code>{c.serialNumber}</code>
												</Link>
											</td>
											<td>{c.cardType}</td>
											<td>{c.dataModel}</td>
											<td>{formatDateTime(c.readDate)}</td>
											<td>
												<button
													type="button"
													className="icon-button icon-button--danger"
													aria-label={t("delete_aria", { serial: c.serialNumber })}
													title={t("delete_tooltip")}
													disabled={fDeletingId === c.id}
													onClick={() => deleteCard(c)}
												>
													<TrashIcon />
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>

							<div className="pager">
								<select
									className="pager__page-size"
									value={fPageSize}
									onChange={(e) => fSetPageSize(Number.parseInt(e.target.value, 10))}
									aria-label={tc("page_size_label")}
								>
									{kPageSizeOptions.map((n) => (
										<option key={n} value={n}>{n}</option>
									))}
								</select>
								<button
									type="button"
									className="button button--ghost"
									disabled={fPage <= 1}
									onClick={() => fLoad(fPage - 1, fFiltersRef.current, fPageSizeRef.current)}
								>
									{tc("prev")}
								</button>
								<span className="muted">{tc("page_of", { page: fPage, total: vTotalPages })}</span>
								<button
									type="button"
									className="button button--ghost"
									disabled={fPage >= vTotalPages}
									onClick={() => fLoad(fPage + 1, fFiltersRef.current, fPageSizeRef.current)}
								>
									{tc("next")}
								</button>
							</div>
						</>
					)}
		</div>
		{fConfirm.dialog}
		</>
	);
}

/*
Filter bar used at the top of the cards table. Stateless — owns no
state, just renders the controls and reports every change up to the
parent via onChange. The parent debounces.
*/
function FilterBar(
	props: {
		filters:	Filters;					// Current values
		onChange:	(inNext: Filters) => void;	// Reports every edit
		anyActive:	boolean;					// Whether to show "Clear"
	}
): JSX.Element
{
	const { t }		= useTranslation("cards");
	const { t: tc }	= useTranslation("common");
	const vUpdate = (inPatch: Partial<Filters>) =>
	{
		props.onChange({ ...props.filters, ...inPatch });
	};

	return (
		<div className="filter-bar">
			<div className="filter-bar__column">
				<input
					type="text"
					placeholder={t("filter_serial")}
					value={props.filters.serialNumber}
					onChange={(e) => vUpdate({ serialNumber: e.target.value })}
					aria-label={t("filter_serial")}
				/>
			</div>

			<div className="filter-bar__divider" aria-hidden="true" />

			<div className="filter-bar__column">
				<select
					value={props.filters.cardType}
					onChange={(e) => vUpdate({ cardType: e.target.value })}
					aria-label={t("filter_type")}
				>
					<option value="">{t("filter_type_all")}</option>
					{kCardTypes.map((c) => <option key={c} value={c}>{c}</option>)}
				</select>
				<select
					value={props.filters.dataModel}
					onChange={(e) => vUpdate({ dataModel: e.target.value })}
					aria-label={t("filter_model")}
				>
					<option value="">{t("filter_model_all")}</option>
					{kDataModels.map((m) => <option key={m} value={m}>{m}</option>)}
				</select>
			</div>

			<div className="filter-bar__divider" aria-hidden="true" />

			<div className="filter-bar__column">
				<label className="filter-bar__date-row">
					<span className="filter-bar__date-label">{tc("from")}</span>
					<input
						type="date"
						value={props.filters.readDateFrom}
						onChange={(e) => vUpdate({ readDateFrom: e.target.value })}
						aria-label={t("filter_date_min")}
					/>
				</label>
				<label className="filter-bar__date-row">
					<span className="filter-bar__date-label">{tc("to")}</span>
					<input
						type="date"
						value={props.filters.readDateTo}
						onChange={(e) => vUpdate({ readDateTo: e.target.value })}
						aria-label={t("filter_date_max")}
					/>
				</label>
			</div>

			{props.anyActive
				? (
					<button
						type="button"
						className="button button--ghost"
						onClick={() => props.onChange(kEmptyFilters)}
					>
						{tc("clear")}
					</button>
				)
				: null}
		</div>
	);
}

/*
Builds the query string for /contents from page + filter state.
Empty filter values are omitted so the server sees no filter at all
(rather than a blank-string filter, which would silently match
everything anyway).

readDateFrom / readDateTo are HTML date strings ("YYYY-MM-DD"); they
are converted to ms-since-epoch using the local timezone, with the
"From" anchored at start-of-day and "To" anchored at end-of-day so a
single-day range "2026-05-04 → 2026-05-04" includes everything read
that calendar day.
*/
function buildQuery(
	inPage:		number,		// 1-based page index
	inFilters:	Filters,	// Current filter state
	inPageSize:	number		// Rows per page
): string
{
	const vParams = new URLSearchParams();	// URL builder

	vParams.set("page",     String(inPage));
	vParams.set("pageSize", String(inPageSize));

	if (inFilters.serialNumber.trim() !== "")	{ vParams.set("serialNumber", inFilters.serialNumber.trim()); }
	if (inFilters.cardType.trim()     !== "")	{ vParams.set("cardType",     inFilters.cardType.trim()); }
	if (inFilters.dataModel.trim()    !== "")	{ vParams.set("dataModel",    inFilters.dataModel.trim()); }
	if (inFilters.folderPath.trim()   !== "")	{ vParams.set("folderPath",   inFilters.folderPath.trim()); }

	const vFromMs	= dateInputToStartMs(inFilters.readDateFrom);	// Local-tz start of day
	const vToMs		= dateInputToEndMs  (inFilters.readDateTo);	// Local-tz end of day

	if (vFromMs !== null) { vParams.set("readDateFrom", String(vFromMs)); }
	if (vToMs   !== null) { vParams.set("readDateTo",   String(vToMs)); }

	return `?${vParams.toString()}`;
}

/*
Reads an initial Filters object from the URL search string. Used
once on mount to hydrate component state from a bookmarked /cards?…
link. Unknown / unrelated keys are ignored.
*/
function filtersFromSearchParams(
	inParams:	URLSearchParams		// Current router search params
): Filters
{
	return {
		serialNumber:	inParams.get("serialNumber")	?? "",
		cardType:		inParams.get("cardType")		?? "",
		dataModel:		inParams.get("dataModel")		?? "",
		folderPath:		inParams.get("folderPath")		?? "",
		readDateFrom:	inParams.get("readDateFrom")	?? "",
		readDateTo:		inParams.get("readDateTo")		?? "",
	};
}

/*
Serialises the current Filters object to URLSearchParams. Empty
fields are omitted so a cleared filter bar produces a clean /cards
URL with no trailing "?" hash. Date strings are passed through as-is
(YYYY-MM-DD), not converted to ms — keeping the URL human-readable
and matching what the user typed. pageSize is included only when it
differs from the default so the typical view stays clean.
*/
function filtersToSearchParams(
	inFilters:	Filters,	// Current filter state
	inPageSize:	number		// Current rows per page
): URLSearchParams
{
	const vParams = new URLSearchParams();	// URL builder

	if (inFilters.serialNumber.trim() !== "")	{ vParams.set("serialNumber", inFilters.serialNumber.trim()); }
	if (inFilters.cardType.trim()     !== "")	{ vParams.set("cardType",     inFilters.cardType.trim()); }
	if (inFilters.dataModel.trim()    !== "")	{ vParams.set("dataModel",    inFilters.dataModel.trim()); }
	if (inFilters.folderPath.trim()   !== "")	{ vParams.set("folderPath",   inFilters.folderPath.trim()); }
	if (inFilters.readDateFrom        !== "")	{ vParams.set("readDateFrom", inFilters.readDateFrom); }
	if (inFilters.readDateTo          !== "")	{ vParams.set("readDateTo",   inFilters.readDateTo); }
	if (inPageSize !== kPageSizeDefault)		{ vParams.set("pageSize",     String(inPageSize)); }

	return vParams;
}

/*
Reads pageSize from URL search params, validating against the
allowed selector options to avoid letting an arbitrary URL widen
the per-request payload past the cap. Falls back to default on any
unexpected value.
*/
function readPageSizeFromParams(
	inParams:	URLSearchParams		// Router search params
): number
{
	const vRaw = inParams.get("pageSize");	// Raw "?pageSize=" value

	if (vRaw === null)
	{
		return kPageSizeDefault;
	}

	const vN = Number.parseInt(vRaw, 10);	// Parsed integer

	return kPageSizeOptions.includes(vN) ? vN : kPageSizeDefault;
}

/*
Converts a "YYYY-MM-DD" string from <input type="date"> to ms since
epoch at local-timezone 00:00:00. Returns null when blank or
unparseable.
*/
function dateInputToStartMs(
	inValue:	string	// "YYYY-MM-DD" or ""
): number | null
{
	if (inValue === "")
	{
		return null;
	}

	const vMs = Date.parse(`${inValue}T00:00:00`);	// Local TZ start of day

	return Number.isFinite(vMs) ? vMs : null;
}

/*
Converts a "YYYY-MM-DD" string from <input type="date"> to ms since
epoch at local-timezone 23:59:59.999. Used as inclusive upper bound
so a single-day range covers the whole calendar day.
*/
function dateInputToEndMs(
	inValue:	string	// "YYYY-MM-DD" or ""
): number | null
{
	if (inValue === "")
	{
		return null;
	}

	const vMs = Date.parse(`${inValue}T23:59:59.999`);	// Local TZ end of day

	return Number.isFinite(vMs) ? vMs : null;
}

/*
Inline SVG trash icon — strokes inherit currentColor so it follows the
button's text colour (red on hover via .icon-button--danger).
*/
function TrashIcon(): JSX.Element
{
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M3 6h18" />
			<path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
			<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
			<path d="M10 11v6" />
			<path d="M14 11v6" />
		</svg>
	);
}

/*
Formats a ms-since-epoch timestamp to "YYYY-MM-DD HH:MM" in the local timezone.
*/
function formatDateTime(
	inMs:	number	// Timestamp in milliseconds
): string
{
	const vDate = new Date(inMs);	// Local Date

	const pad = (n: number) => String(n).padStart(2, "0");
	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())} ${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}
